import {
	Component, Input, OnDestroy, OnChanges, AfterViewInit, ViewChild, SimpleChanges, OnInit, ChangeDetectorRef
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MessageService } from 'base';
import { DiscResultsService } from 'discovery';
import { Device } from 'DiscoveryEngine.TS/Core';
import { List, ValueEncoder } from 'DiscoveryEngine.TS/Utils';
import { IdentityService } from 'identity';
import {
	GetDeviceResultDto, IGetPortStateResultDto, IPortStateDto_Get, ISetPortStateResultDto, PortStateDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { IfEntry, IfTable } from 'DiscoveryEngine.TS/InfoSets/IfTable';
import { SnmpConstants } from 'DiscoveryEngine.TS/Core/SnmpConstants';
import { PortStateType } from 'UvExplorerServer.Dtos.TS/Session';
import { NavigationService } from 'netview';
import { UserPermissionsService } from 'identity';
import { ToolsService } from 'tools';

@Component({
	selector: 'single-port-alias-editor',
	templateUrl: './single-port-alias-editor.component.html',
	styleUrls: ['./single-port-alias-editor.component.scss']
})
export class SinglePortAliasEditorComponent implements OnChanges, OnDestroy, OnInit {

	@Input()
	public deviceGuid: string;

	@Input()
	public ifIndex: number;

	@Input()
	public visible: boolean;

	public name: string;
	public description: string;

	public form: UntypedFormGroup;
	public alias: UntypedFormControl;

	private device: Device;
	private interface: IfEntry;

	constructor(private formBuilder: UntypedFormBuilder,
		private navService: NavigationService,
		private messageService: MessageService,
		private toolsService: ToolsService,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private userPermissionsService: UserPermissionsService,
		private changeDetector: ChangeDetectorRef) {

		this.deviceGuid = "";
		this.ifIndex = -1;
		this.visible = false;
		this.name = "";
		this.description = "";
		this.form = null;
		this.alias = null;
		this.device = null;
		this.interface = null;
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (!!changes["deviceGuid"] || !!changes["ifIndex"] || !!changes["visible"]) {
			if (!!this.deviceGuid && this.visible) {
				if (this.alias) {
					this.alias.setValue("");
				}
				setTimeout(() => this.getDevice(), 0);
			}
			else {
				if (this.alias) {
					this.alias.setValue("");
				}
				this.setDevice(null);
			}
		}
	}

	public ngOnInit(): void {
		this.rebuildForm();
	}

	public ngOnDestroy(): void {

	}

	private rebuildForm(): void {
		let alias = "";

		if (this.interface) {
			alias = this.interface.Alias;
        }

		this.alias = new UntypedFormControl(alias);

		this.form = this.formBuilder.group({
			alias: alias,
		});
	}

	public get enableUpdate(): boolean {
		return (this.userPermissionsService.canEditPortAliases &&
			!!this.device && !!this.interface);
	}

	public get enableRefresh(): boolean {
		return (!!this.device && !!this.interface);
	}

	public refresh(): void {
		this.getDevicePortState();
	}

	public update(): void {
		if (!!this.device && !!this.interface) {

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			let portStateDto = this.createPortStateDto(this.device, this.interface);

			this.toolsService.setPortState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
				schedDiscRsltGuid, PortStateType.Alias, [portStateDto], true).subscribe(
					(result: ISetPortStateResultDto) => {
						let succeeded = false;
						if (result.entries) {
							succeeded = result.entries.every((entry) => entry.succeeded);
						}
						if (succeeded) {
							this.messageService.displayInfoMessage("Port was successfully configured");
						}
						else {
							this.messageService.displayErrorMessage("Port could not be configured");
						}
					},
					(error) => {
						this.messageService.displayErrorMessage("Port could not be configured");
					});
		}
	}

	private createPortStateDto(device: Device, ifEntry: IfEntry): PortStateDto {
		let result = new PortStateDto();

		result.DeviceGuid = ValueEncoder.EncodeGuid(device.GUID);
		result.IfIndex = ifEntry.IfIndex;
		if (this.alias.value) {
			result.Alias = this.alias.value;
		}
		else {
			result.Alias = "";
        }


		return result;
	}

	private setDevice(device: Device): void {
		if (device) {
			this.device = device;

			this.interface = null;
			if (this.device.HasIfTable) {
				this.interface = this.device.IfTable.Find(this.ifIndex);
				if (this.interface) {
					this.name = this.interface.Name;
					this.description = this.interface.Description;
				}
			}
		}
		else {
			this.device = null;
			this.interface = null;
			this.name = "";
			this.description = "";
		}
	}

	private getDevice(): void {

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		let deviceGuids = new List<string>();
		deviceGuids.Add(this.deviceGuid);

		this.messageService.startWork();
		this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "network", deviceGuids, true).subscribe(
				(result: GetDeviceResultDto) => {
					this.messageService.endWork();
					if (result.HasDevices) {
						this.setDevice(result.Devices.Get(0));
						this.rebuildForm();
						this.changeDetector.detectChanges();
					}
					else {
						this.messageService.displayErrorMessage("Could not retrieve selected device");
					}
				},
				(error?) => {
					this.messageService.endWork();
					this.messageService.displayErrorMessage("Could not retrieve selected device");
				});
	}

	private getDevicePortState(): void {

		if (!!this.device && this.device.HasIfTable) {
			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			this.messageService.startWork();
			this.toolsService.getPortState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
				schedDiscRsltGuid, [PortStateType.Alias], [this.deviceGuid], [this.interface.IfIndex], true).subscribe(
					(result: IGetPortStateResultDto) => {
						this.messageService.endWork();
						if (!!this.device && this.device.HasIfTable) {
							let errors = false;
							let message = "";

							if (!result.entries || result.entries.length == 0) {
								errors = true;
							}
							else {
								errors = result.entries.some((entry: IPortStateDto_Get) => {
									if (!entry.succeeded) {
										message = entry.message;
										return true;
									}
									else {
										return false;
									}
								});
							}

							if (errors) {
								this.messageService.displayErrorMessage("Could not retrieve device port state");
								console.error(message);
							}
							else {
								this.interface.Alias = result.entries[0].alias;
								this.rebuildForm();
								this.changeDetector.detectChanges();
							}
						}
					},
					(error?) => {
						this.messageService.endWork();
						this.messageService.displayErrorMessage("Could not retrieve device port state");
					});
		}
	}

}
